import {
	AlertVariant,
	Button,
	Card,
	CardBody,
	Flex,
	FormGroup,
	Grid,
	GridItem,
	TextInput,
	FlexItem,
} from '@patternfly/react-core';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import PresentationPageList from './PresentationPageList';
import { PresentionationContext } from '../../types/presentation';
import { useLocation, useOutletContext, useParams } from 'react-router';
import { GridLayoutOutletContext } from '../../layout/Layout';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { TWidget, Widget } from '../../api/dashbboards/DashboardWidgets';
import PresentationSlide from './PresentationSlide';
import { useMount } from 'react-use';
import { Folder, TFolder } from '../../api/foundational-elements/Folder';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib';
import ExclamationCircleIcon from '@patternfly/react-icons/dist/esm/icons/exclamation-circle-icon';
import SelectFolderDropdown from '../../helpers/helper-components/SelectFolderDropdown';
import { Present } from '../../api/present/Present';
import { addNewRecentPresentation } from '../../helpers/helper-components/recents-factory-helper';
import DashboardFilters from '../analyze/dashboard/DashboardFilters';
import { TNewDateRange } from '../../api/types/TNewDateRange';
import { DashboardFilter } from '../../api/dashbboards/DashboardFilter';
import { TPresentationSlide } from '../../api/present/PresentationSlide';
import Loader from '../../components/util/Loader';
import { useCommonStoreContext } from '../../components/common-store/CommonStoreProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { createPresentation } from '../../helpers/present.helper';
import WidgetLibrary from '../../components/dnd/widgets/WidgetLibrary';
import { FolderTypesEnum } from '../../enums/folder-types-enum';
import PageTitleSubheader from '../../layout/subheader/PageTitleSubheader';
import { faDownToBracket, faPersonChalkboard } from '@fortawesome/pro-light-svg-icons';

interface Props {
	pageContext: PresentionationContext;
}

const PresentationDetail = (props: Props) => {
	const { pageContext } = props;
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { presentationId } = useParams();
	const { addToast } = useToast();
	const {
		setSubSide,
		subNavExpanded,
		setSubNavExpanded,
		gridLayout,
		updateGridLayout,
		updateGridBorder,
	} = useOutletContext<GridLayoutOutletContext>();
	const [selectedSlide, setSelectedSlide] = useState<number>(0);
	const [loading, setLoading] = useState<boolean>(false);
	const [isExporting, setIsExporting] = useState<boolean>(false);
	const presentationModel: Present = Present.Default();
	const [currentSlide, setCurrentSlide] = useState<TPresentationSlide>(
		presentationModel.presentationSlides[selectedSlide]
	);
	const [pageModel, setPageModel] = useState<Present>(presentationModel);
	const [folders, setFolders] = useState<TFolder[]>([]);
	const [widgets, setWidgets] = useState<Widget[]>([]);
	const [unSavedChanges, setUnSavedChanges] = useState(false);
	const [existingPresentationName, setExistingPresentationName] = useState<string>();
	const [selectedDate, setSelectedDate] = useState<TNewDateRange>();
	const [dashboardFilters, setDashboardFilters] = useState<DashboardFilter[] | undefined>([]);
	const [dashboardFiltersToDelete, setDashboardFiltersToDelete] = useState<
		DashboardFilter[] | undefined
	>([]);
	const { setCurrentSelectedPresentation, setClickedNewSlide } = useCommonStoreContext();
	const queryParams = useLocation().search;
	const isCopy = queryParams.includes('isCopy=true');
	const isCopyAndNameProvided = isCopy ? pageModel.name != '' : true;
	const [validated, setValidated] = useState<
		'default' | 'warning' | 'success' | 'error' | undefined
	>('default');

	const updateSelectedDate = useCallback(
		(startDateRange: TNewDateRange, endDateRange: TNewDateRange) => {
			if (setSelectedDate) {
				setSelectedDate({
					begin_date: startDateRange.begin_date,
					end_date: endDateRange.end_date,
					period: startDateRange.period,
					sequence: startDateRange.sequence,
				});
			}
		},
		[selectedDate, setSelectedDate]
	);

	useMount(() => {
		getPageData();
		setSubSide({
			subheaderContext: (
				<PageTitleSubheader
					pageTitle="Present"
					expanded={subNavExpanded}
					setExpanded={setSubNavExpanded}
				/>
			),
			sidebarContext: null,
			sidebarSearch: undefined,
			rightSideBar: (
				<WidgetLibrary
					gridLayout={gridLayout}
					gridBorder
					updateGridLayout={updateGridLayout}
					updateGridBorder={updateGridBorder}
				/>
			),
			hideLeftSideBar: true,
			folderType: FolderTypesEnum.Presentation,
		});

		//log entry into recents for loaded presentation
		if (presentationId) {
			addNewRecentPresentation(presentationId);
		}
	});

	useEffect(() => {
		getPageData();

		if (window.location.pathname != '/present/create') {
			localStorage.removeItem('currentSelectedPresentFolderId');
			localStorage.removeItem('currentSelectedPresentName');
		}
	}, [window.location.pathname]);

	const getPageData = () => {
		Folder.GetAll({ type: 'presentations' })
			.then((response) => {
				if (response) {
					setFolders(response.filter((f) => f.isFolder));
				}
			})
			.catch(() => {
				addToast('Get folders failed.', AlertVariant.danger);
			});
		if (presentationId) {
			setLoading(true);
			Present.Get(parseInt(presentationId), ['presentationSlides'])
				.then((presentationResponse) => {
					if (presentationResponse) {
						setCurrentSelectedPresentation(presentationResponse);
						const newModel = { ...presentationModel, ...presentationResponse };

						const promiseAll: Promise<TPresentationSlide>[] = [];
						presentationResponse.presentationSlides.forEach((slide) => {
							if (typeof slide != 'number' && slide.id)
								promiseAll.push(
									Present.GetPresentationSlide(Number(slide.id), ['widgets'])
								);
						});

						Promise.all(promiseAll)
							.then((responses: TPresentationSlide[]) => {
								responses.forEach((slideResponse) => {
									const presentSlide = newModel.presentationSlides.find(
										(slide) => slide.id === slideResponse.id
									) as TPresentationSlide;
									presentSlide.widgets = (slideResponse.widgets as TWidget[]).map(
										(w) => {
											return { ...w, presentationSlide: presentSlide.id };
										}
									);
								});
								setPageModel(newModel);
								setExistingPresentationName(presentationResponse.name);

								const firstSlide = responses.find((x) => x.page == 0);
								setCurrentSlide(firstSlide ?? responses[0]);
								setLoading(false);
							})
							.catch((_) => {
								addToast('Failed to get Presentation Slides');
								setLoading(false);
							});
					}
				})
				.catch(() => {
					addToast('Get presentation failed.', AlertVariant.danger);
					setLoading(false);
				});
		} else {
			const ls_PresentFolderId = localStorage.getItem('currentSelectedPresentFolderId');
			const ls_PresentName = localStorage.getItem('currentSelectedPresentName');

			if (!isCopy) {
				//creating a new blank presentation

				if (ls_PresentFolderId) {
					setPageModel((prev) => ({
						...prev,
						folder: +ls_PresentFolderId,
					}));
				}

				if (ls_PresentName) {
					setPageModel((prev) => ({
						...prev,
						name: ls_PresentName,
					}));
				}
			} else {
				const ls_PresentId = localStorage.getItem('currentSelectedPresentId') ?? '0';

				setLoading(true);
				Present.Get(parseInt(ls_PresentId), ['presentationSlides'])
					.then((presentationResponse) => {
						if (presentationResponse) {
							setCurrentSelectedPresentation(presentationResponse);
							const newModel = { ...presentationModel, ...presentationResponse };

							const promiseAll: Promise<TPresentationSlide>[] = [];
							presentationResponse.presentationSlides.forEach((slide) => {
								if (typeof slide != 'number' && slide.id)
									promiseAll.push(
										Present.GetPresentationSlide(Number(slide.id), ['widgets'])
									);
							});

							Promise.all(promiseAll)
								.then((responses: TPresentationSlide[]) => {
									responses.forEach((slideResponse) => {
										const presentSlide = newModel.presentationSlides.find(
											(slide) => slide.id === slideResponse.id
										) as TPresentationSlide;
										presentSlide.widgets = (
											slideResponse.widgets as TWidget[]
										).map((w) => {
											return { ...w, presentationSlide: presentSlide.id };
										});
									});
									setPageModel(newModel);
									setExistingPresentationName(presentationResponse.name);
									setCurrentSlide({
										...newModel.presentationSlides[0],
									});

									if (ls_PresentFolderId) {
										setPageModel((prev) => ({
											...prev,
											folder: +ls_PresentFolderId,
										}));
									}

									if (ls_PresentName) {
										setPageModel((prev) => ({
											...prev,
											name: ls_PresentName,
										}));
									}

									setPageModel((prev) => ({
										...prev,
										id: undefined,
									}));

									setLoading(false);
								})
								.catch((_) => {
									addToast('Failed to get Presentation Slides');
									setLoading(false);
								});
						}
					})
					.catch(() => {
						addToast('Get presentation failed.', AlertVariant.danger);
						setLoading(false);
					});
			}
		}
	};

	const getHiddenDOM = (slides: TPresentationSlide[]): ReactElement[] => {
		return slides.map((slide) => (
			<PresentationSlide
				slide={slide}
				gridLayout={gridLayout}
				filters={dashboardFilters}
				selectedDate={selectedDate}
				isEdit={false}
				allowClickNavigate={false}
				noAnimation={true}
				displayOnly
				isEmptyDashboardFilters={
					dashboardFilters !== undefined ? dashboardFilters.length === 0 : undefined
				}
			/>
		));
	};

	const updateModel = useCallback(
		(widgets: Widget[]) => {
			pageModel.presentationSlides[selectedSlide].widgets = widgets;
			setPageModel(pageModel);
		},
		[pageModel, setPageModel, selectedSlide]
	);

	const addNewPage = useCallback(() => {
		pageModel.presentationSlides.push({
			widgets: [],
			page: pageModel.presentationSlides.length,
		});
		setPageModel(pageModel);
		setSelectedSlide(pageModel.presentationSlides.length - 1);
		setCurrentSlide({
			...pageModel.presentationSlides[pageModel.presentationSlides.length - 1],
		});
		setClickedNewSlide(true);
	}, [pageModel, setPageModel, selectedSlide, setSelectedSlide]);

	const removePage = useCallback(
		(index: number) => {
			pageModel.presentationSlides.splice(index, 1);
			if (selectedSlide + 1 === index && index > 1) {
				setSelectedSlide(index - 1);
				setCurrentSlide({ ...pageModel.presentationSlides[index - 1] });
			} else {
				setSelectedSlide(0);
				setCurrentSlide({ ...pageModel.presentationSlides[0] });
			}
			setPageModel(pageModel);
		},
		[pageModel, setPageModel, selectedSlide, setSelectedSlide]
	);

	const duplicatePage = useCallback(
		(index: number) => {
			const pageToDuplicate = { ...pageModel.presentationSlides[index] };
			pageToDuplicate.id = undefined;
			pageToDuplicate.widgets = (pageToDuplicate.widgets as Widget[]).map((w) => {
				w.id = undefined;
				w.presentationSlide = undefined;
				return w;
			});
			pageModel.presentationSlides.splice(index, 0, pageToDuplicate);
			pageModel.presentationSlides = pageModel.presentationSlides.map((ps, index) => {
				ps.page = index + 1;
				return ps;
			});
			setPageModel(pageModel);
			setSelectedSlide(index + 1);
		},
		[pageModel, setPageModel, selectedSlide, setSelectedSlide]
	);

	const movePageUp = useCallback(
		(index: number) => {
			// const page = { ...pageModel.presentationSlides[index] };
			const page = pageModel.presentationSlides.splice(index, 1)[0];
			pageModel.presentationSlides.splice(index - 1, 0, page);
			pageModel.presentationSlides = pageModel.presentationSlides.map((ps, index) => {
				ps.page = index + 1;
				return ps;
			});
			setPageModel(pageModel);
			setSelectedSlide(index - 1);
		},
		[pageModel, setPageModel, selectedSlide, setSelectedSlide]
	);

	const movePageDown = useCallback(
		(index: number) => {
			// const page = { ...pageModel.presentationSlides[index] };
			const page = pageModel.presentationSlides.splice(index, 1)[0];
			pageModel.presentationSlides.splice(index + 1, 0, page);
			pageModel.presentationSlides = pageModel.presentationSlides.map((ps, index) => {
				ps.page = index + 1;
				return ps;
			});
			setPageModel(pageModel);
			setSelectedSlide(index + 1);
		},
		[pageModel, setPageModel, selectedSlide, setSelectedSlide]
	);

	const updateSelectedPage = useCallback(
		(slideNumber: number) => {
			setSelectedSlide(slideNumber);
			setCurrentSlide({ ...pageModel.presentationSlides[slideNumber] });
		},
		[pageModel, setPageModel, selectedSlide, setSelectedSlide, setCurrentSlide]
	);

	const handlePresentationWidgetChange = useCallback(
		(widget: Widget) => {
			!unSavedChanges && setUnSavedChanges(true);
			widget.page = selectedSlide;

			const newPageModel = { ...pageModel };
			if (widget.isNew && !widget.widgetChanged) {
				widget.id = newPageModel.presentationSlides[selectedSlide].widgets.length + 1;
				newPageModel.presentationSlides[selectedSlide].widgets = [
					...(newPageModel.presentationSlides[selectedSlide].widgets as Widget[]),
					widget,
				];
			} else {
				const newWidgets = [
					...(newPageModel.presentationSlides[selectedSlide].widgets as Widget[]),
				];

				let widgetIndex = (
					newPageModel.presentationSlides[selectedSlide].widgets as Widget[]
				).findIndex((w: Widget) => w.id === widget.id);
				if (widgetIndex < 0) {
					widgetIndex = newWidgets.length;
				}
				newWidgets[widgetIndex] = {
					...widget,
				};
				newPageModel.presentationSlides[selectedSlide].widgets = newWidgets;
			}
			updateModel(newPageModel.presentationSlides[selectedSlide].widgets as Widget[]);
			setCurrentSlide({ ...newPageModel.presentationSlides[selectedSlide] });
		},
		[pageModel, selectedSlide]
	);

	const handleChartOrTableEditClick = useCallback(
		(widget: Widget) => {
			if (widget.chart) {
				presentationId && navigate(`/present/${presentationId}/chart/${widget.chart}`);
			}
			if (widget.report) {
				presentationId && navigate(`/present/${presentationId}/table/${widget.report}`);
			}
		},
		[presentationId]
	);

	const removeWidget = useCallback(
		(widget: Widget) => {
			setPageModel((currentPageModel) => {
				const updatedSlides = currentPageModel.presentationSlides.map((slide) => {
					if (slide.page === currentSlide.page) {
						return {
							...slide,
							widgets: (slide.widgets as Widget[]).filter(
								(w: Widget) => w.id !== widget.id
							),
						};
					}
					return slide;
				});
				return { ...currentPageModel, presentationSlides: updatedSlides };
			});
			setCurrentSlide((prev) => ({
				...prev,
				widgets: (prev.widgets as TWidget[]).filter((w) => w.id !== widget.id),
			}));
		},
		[currentSlide]
	);

	const handleNameChange = (value: string, _event: React.FormEvent<HTMLInputElement>) => {
		setPageModel({ ...pageModel, name: value });

		if (value === '') {
			setValidated('error');
		} else {
			setValidated('success');
		}
	};

	const handlePresentaionSave: (isPresentationSaveAndNavigate?: boolean) => void = (
		isPresentationSaveAndNavigate?: boolean
	) => {
		if (validated == 'error') {
			return;
		}

		setUnSavedChanges(false);
		setLoading(true);
		setClickedNewSlide(false);

		if (!isCopyAndNameProvided) {
			setLoading(false);
			addToast('Presentation save failed.', AlertVariant.danger);
			return false;
		}

		let newPresentationId = 0;
		let presentationSlidesResponse: TPresentationSlide[] = [];
		const presentApiModel = getPresentModel();

		if (!presentApiModel.id || isCopy) {
			Present.New(presentApiModel)
				.then((presentationResponse) => {
					newPresentationId = presentationResponse.id ?? 0;
					presentationSlidesResponse = presentationResponse.presentationSlides;
					setPageModel((currentModel) => {
						return { ...currentModel, id: newPresentationId };
					});

					navigate(`/present/edit/${newPresentationId}`);
				})
				.catch((_) => {
					setLoading(false);
					addToast('Presentation create failed.', AlertVariant.danger);
				});
		} else {
			Present.Edit(presentApiModel)
				.then((_editResponse) => {
					setLoading(false);
					addToast('Presentation updated.', AlertVariant.success);
				})
				.catch((_) => {
					setLoading(false);
					addToast('Presentation update failed.', AlertVariant.danger);
				});
		}
	};

	const getPresentModel = (): Present => {
		const presentModel = Present.Default();
		presentModel.id = isCopy ? undefined : pageModel.id;
		presentModel.name = pageModel.name;
		presentModel.owner = pageModel.owner;
		presentModel.folder = pageModel.folder;
		presentModel.presentationSlides = pageModel.presentationSlides.map((s) => {
			return {
				page: s.page,
				presentation: s.presentation,
				id: s.id,
				widgets: s.widgets.map((w) => {
					const thisWidget = w as Widget;
					return {
						id: thisWidget.isNew || isCopy ? undefined : thisWidget.id,
						presentationSlide: isCopy ? undefined : thisWidget.presentationSlide,
						col: thisWidget.col,
						row: thisWidget.row,
						sizex: thisWidget.sizex,
						sizey: thisWidget.sizey,
						page: thisWidget.page,
						name: thisWidget.name,
						widget_type: thisWidget.widget_type,
						content: thisWidget.content,
						chart: thisWidget.chart,
						report: thisWidget.report,
						source: thisWidget.source,
						isNew: thisWidget.isNew,
						widgetChanged: thisWidget.widgetChanged,
						isEdit: thisWidget.isEdit,
						conditionalRules: thisWidget.conditionalRules,
					};
				}),
			};
		});
		presentModel.presentationFilters = (dashboardFilters ?? []).map((df) => {
			const filter: DashboardFilter = { ...df };
			if (df.isNew) {
				filter.id = undefined;
				filter.isNew = false;
			}
			return filter;
		});
		return presentModel;
	};

	const updateFiltersToDelete = (filter: DashboardFilter) => {
		setDashboardFiltersToDelete([...(dashboardFiltersToDelete ?? []), filter]);
	};
	const updateFilters = (filters: DashboardFilter[]) => {
		setDashboardFilters(filters);
	};

	return (
		<>
			{(isExporting || loading) && <Loader isFullScreen />}
			<Card
				className={'present-details-container'}
				style={{ marginBottom: '0.24rem' }}
			>
				{isCopy && (
					<>
						<div className="pf-c-alert pf-m-inline pf-m-info">
							<p className="pf-c-alert__title display-inline">
								<FontAwesomeIcon
									size="2x"
									icon={faInfoCircle}
									className="bolder"
								/>
								<span className="nudge-right"></span>
								<div className="fs-large">
									{`Note! You are currently creating a copy of the Presentation '${
										localStorage.getItem('currentSelectedPresentName') ?? ''
									}'`}
								</div>
							</p>
						</div>
						<br />
					</>
				)}
				<CardBody style={{ paddingBottom: '1.5rem' }}>
					<Grid
						className="row-container"
						span={12}
					>
						<div className="start-container">
							<FormGroup
								label={'Presentation Name'}
								type="text"
								isRequired
								fieldId="name"
								helperTextInvalid={'Presentation Name is required'}
								helperTextInvalidIcon={<ExclamationCircleIcon />}
								validated={validated}
							>
								<TextInput
									isRequired
									type="text"
									aria-label={'Enter a Presentation Name'}
									placeholder={'Enter a Presentation Name'}
									value={pageModel.name}
									onChange={handleNameChange}
								/>
							</FormGroup>
							<SelectFolderDropdown
								folderId={pageModel.folder as number}
								folders={folders}
								onFolderSelect={(e, item) => {
									setPageModel({
										...pageModel,
										folder: parseInt(item.id ?? '0'),
									});
								}}
							/>
						</div>
						<GridItem span={6}>
							<Flex justifyContent={{ default: 'justifyContentFlexEnd' }}>
								<Button onClick={() => handlePresentaionSave(true)}>Save</Button>
								{presentationId && (
									<Button
										onClick={() =>
											navigate(`/present/view/${presentationId ?? 0}`)
										}
									>
										Return
									</Button>
								)}
							</Flex>
						</GridItem>
					</Grid>
					<Grid>
						<GridItem span={10}>
							<DashboardFilters
								presentationId={presentationId ? parseInt(presentationId) : 0}
								updateFilters={updateFilters}
								setSelectedDate={updateSelectedDate}
								setUnSavedChanges={setUnSavedChanges}
								updateFiltersToDelete={updateFiltersToDelete}
							/>
						</GridItem>
						<GridItem span={2}>
							<Flex>
								<FlexItem align={{ default: 'alignRight' }}>
									<Button
										variant="plain"
										aria-label="Present"
										data-testid="present-entity"
										onClick={() => {
											window.open(
												`/present/present-view/${presentationId ?? 0}`,
												'_blank'
											);
										}}
									>
										<FontAwesomeIcon
											icon={faPersonChalkboard}
											className="present-icon"
											size="xl"
											title="Present"
										/>
									</Button>
								</FlexItem>
								<FlexItem>
									<Button
										variant="plain"
										aria-label="Export PPTX"
										onClick={(e) => {
											createPresentation(pageModel, setIsExporting);
											e.preventDefault();
										}}
									>
										<FontAwesomeIcon
											icon={faDownToBracket}
											className="download-icon"
											size="xl"
											title="Export PPTX"
										/>
									</Button>
								</FlexItem>
							</Flex>
						</GridItem>
					</Grid>
				</CardBody>
			</Card>
			<Card>
				<CardBody>
					<Grid hasGutter>
						<GridItem span={2}>
							<PresentationPageList
								slides={pageModel.presentationSlides}
								selectedPage={selectedSlide}
								addNewPage={addNewPage}
								removePage={removePage}
								duplicatePage={duplicatePage}
								movePageUp={movePageUp}
								movePageDown={movePageDown}
								updateSelectedPage={updateSelectedPage}
							/>
						</GridItem>
						<GridItem span={10}>
							<PresentationSlide
								slide={currentSlide}
								handlePresentationWidgetSave={handlePresentationWidgetChange}
								removeWidget={removeWidget}
								handleEditClick={handleChartOrTableEditClick}
								gridLayout={gridLayout}
								isEdit
								allowClickNavigate
								filters={dashboardFilters}
								selectedDate={selectedDate}
								isEmptyDashboardFilters={
									dashboardFilters !== undefined
										? dashboardFilters.length === 0
										: undefined
								}
							/>
						</GridItem>
					</Grid>
				</CardBody>
			</Card>
			<div
				id="presentation-hidden-render"
				style={{ height: 0, overflow: 'hidden' }}
			>
				{getHiddenDOM(pageModel.presentationSlides)}
			</div>
		</>
	);
};

export default PresentationDetail;
